import { Delete } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, IconButton, Link, Stack, styled, Tooltip } from '@mui/material';
import { DataGrid, gridClasses, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  getAllTeamMemberRecordsOfTeam,
  getAllWorkspaceTeams,
  getMyTeams,
  ITeam,
  ITeamMembersResponse,
  selectedOrganizationalUnitTeams,
  deleteOUTeamMember,
  fetchDashboardCollectionsAssignedToTeams,
  getMyPinnedDashboardsCollection,
} from 'wisestep-api';
import {
  Notification,
  SearchBarNew,
  WsColors,
  WsDrawer,
  WsListingLoader,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import TeamAvatartGroup from '../../../components/AppLayoutNew/TeamAvatarGroup';
import PinDashboardCollectionButton, {
  DashboardCollectionIdType,
} from '../../../components/pinDashboardCollectionButton/PinDashboardCollectionButton';
import WsContextMenuHandler from '../../../components/WsContextMenu/WsContextMenuHandler';
import { useWorkspaceContext } from '../../../context';
import { TeamAccessAction, useTeamAccessAuthz } from '../../../hooks/useTeamAccessAuthz';
import TeamAvatar from '../../../images/Avatar/Group.svg';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { StyledPaginationBox } from '../../clientsSettings/components/OrganizationalUnitLayoutNew';
import WsDataGridPagination from '../../submissionsNew/components/widgets/WsDataGridPagination';
import { teamFilters } from '../helper/defaultValues';

import CreateTeamButton from './CreateTeamButton';
import CreateTeamModal from './CreateTeamModal';
import TeamHistory from './TeamHistory';
import TeamListActions from './TeamListActions';
import TeamMembersTable from './TeamMembersTable';
import TeamsEmptyList from './TeamsEmptyList';
import TeamSettings from './TeamSettings';
import WsAvatar from './WsAvatar';

interface ITeamTitleColumnProps {
  param: string;
  onClick?: MouseEventHandler<Element>;
  onContextMenu?: MouseEventHandler<Element>;
}

const TeamTitleColumn = ({ param, onClick, onContextMenu }: ITeamTitleColumnProps) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <WsAvatar src={TeamAvatar} size="small" variant="rounded" />
    <WsTypography
      cursor="pointer"
      variant={WsTypoVariants.SUB_TITLE}
      onClick={onClick}
      color={WsTypoColors.PRIMARY750}
      onContextMenu={onContextMenu}
    >
      {param}
    </WsTypography>
  </Stack>
);

interface ITeamListProps {
  forAnalyticsAndTeamMembersView?: boolean;
  readOnlyMode?: boolean;
  isOrganizationalUnit?: boolean;
  organizationalUnitId?: string;
  isTeamsOrganizationalUnit?: boolean;
}

export const StyledDataGrid = styled(DataGrid)({
  backgroundColor: WsColors.White,
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    fontWeight: 800,
  },
  [`& .${gridClasses.cell}`]: {
    p: 1,
  },
  '.MuiDataGrid-viewport': {
    overflow: 'auto',
  },
  '& .MuiDataGrid-footerContainer': {
    marginRight: 3,
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: '16px',
    fontWeight: 'bold',
  },
});

const TeamListNew = ({
  forAnalyticsAndTeamMembersView = false,
  readOnlyMode = false,
  isOrganizationalUnit,
  organizationalUnitId,
  isTeamsOrganizationalUnit,
}: ITeamListProps) => {
  const admin = useWorkspaceContext();
  const formMethods = useForm();
  const teamsList = useRef<ITeam[]>([]);
  const [filteredTeamList, setFilteredTeamList] = useState<ITeam[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'pinUnpin', sort: 'desc' },
    { field: 'name', sort: 'asc' },
  ]);
  const [teamMembers, setTeamMembers] = useState<ITeamMembersResponse[]>([]);
  const [selectedRow, setSelectedRow] = useState<number>(0);
  const [openTeamMemberDrawer, setOpenTeamMemberDrawer] = useState<boolean>(false);
  const [openTeamMembers, setOpenTeamMembers] = useState<boolean>(false);
  const [openEditTeamName, setOpenEditTeamName] = useState<boolean>(false);
  const [openTeamSettings, setOpenTeamSettings] = useState<boolean>(false);
  const [openTeamHistory, setOpenTeamHistory] = useState<boolean>(false);
  const [createTeamSuccess, setCreateTeamSuccess] = useState<boolean>(false);
  const [editTeamNameSuccess, setEditTeamNameSuccess] = useState<boolean>(false);
  const [pinnedDashboardCheck, setPinnedDashboardCheck] = useState<any[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [dashboardObj, setDashboardObj] = useState<DashboardCollectionIdType>();
  const [newTeamName, setNewTeamName] = useState<string>();
  const navigate = useNavigate();

  const { access: teamAccessControl } = useTeamAccessAuthz([
    TeamAccessAction.MANAGE_TEAMS_ACCESS,
    TeamAccessAction.VIEW_TEAMS_ACCESS,
  ]);

  const viewTeamAccess = admin.owns || teamAccessControl[1];
  const teamCreationAllowed = admin.owns || teamAccessControl[0];

  useEffect(() => {
    const dashboardInPinnedList = async () => {
      const responseCollection = await getMyPinnedDashboardsCollection();
      if (responseCollection.data?.length > 0)
        setPinnedDashboardCheck(responseCollection.data?.map((item) => item?.entityId));
    };
    const dashboardAssignedIds = async () => {
      try {
        const response = await fetchDashboardCollectionsAssignedToTeams(true);
        const ids = response.data?.map((item) => item.id);
        setDashboardObj({ dashboardIds: ids, type: 'Teams' });
      } catch (error) {
        setNotification(failed(`Unable to get  assigned Dashboards`));
      }
    };
    dashboardAssignedIds();
    dashboardInPinnedList();
  }, [setNotification]);

  const navigateToMembersPage = useCallback(
    (rowId: number, isTeamDrawerOpen?: boolean, isOpenNewTab = false) => {
      if (forAnalyticsAndTeamMembersView) {
        const url = '/team/team-members-view';
        const state = {
          teamId: filteredTeamList && filteredTeamList[rowId].id,
          teamName: filteredTeamList && filteredTeamList[rowId].name,
        };
        isOpenNewTab
          ? openUrlInNewTab(`${url}?state=${getEncodedQueryParams(state)}`)
          : navigate(url, { state });
      } else if (readOnlyMode) {
        setOpenTeamMemberDrawer(true);
      } else {
        navigate('./team-members/' + filteredTeamList[rowId].id, {
          state: {
            teamId: filteredTeamList && filteredTeamList[rowId].id,
            teamName: filteredTeamList && filteredTeamList[rowId].name,
            teamOwnerEmail: filteredTeamList && filteredTeamList[rowId].ownerEmail,
            ...(typeof isTeamDrawerOpen !== undefined && { isTeamDrawerOpen: isTeamDrawerOpen }),
          },
        });
      }
    },
    [forAnalyticsAndTeamMembersView, readOnlyMode, navigate, filteredTeamList]
  );

  const openEdit = useCallback((rowId: number) => {
    setSelectedRow(rowId);
    setOpenEditTeamName(true);
  }, []);

  const openSettings = useCallback((rowId: number) => {
    setSelectedRow(rowId);
    setOpenTeamSettings(true);
  }, []);

  const openHistory = useCallback((rowId: number) => {
    setSelectedRow(rowId);
    setOpenTeamHistory(true);
  }, []);

  const getTeamsInWorkspace = useCallback(async () => {
    try {
      setLoader(true);
      if (isOrganizationalUnit) {
        const newresp = await selectedOrganizationalUnitTeams(organizationalUnitId ?? '');
        const teamIds: string[] = newresp.data.map((item) => item.teamId);
        if (teamIds && teamIds.length > 0) {
          const response = await getAllWorkspaceTeams();
          const similarItems: Array<ITeam> = [];
          response.data.forEach((item1) => {
            const matchingItem = newresp.data.find((item2) => item1.id === item2.teamId);
            if (matchingItem) {
              const mergedItem = {
                ...item1,
                organizationalId: matchingItem.organizationalId,
                value: matchingItem.value,
              };
              similarItems.push(mergedItem);
            }
          });
          setFilteredTeamList(similarItems);
          teamsList.current = similarItems;
          setLoader(false);
        } else {
          setFilteredTeamList([]);
          teamsList.current = [];
          setLoader(false);
        }
      } else {
        if (viewTeamAccess) {
          const response = await getAllWorkspaceTeams();
          setFilteredTeamList(response.data);
          teamsList.current = response.data;
          setLoader(false);
        } else {
          const response = await getMyTeams();
          setFilteredTeamList(response.data);
          teamsList.current = response.data;
          setLoader(false);
        }
      }
    } catch {
      setLoader(false);
    }
  }, [viewTeamAccess, isOrganizationalUnit, organizationalUnitId]);

  const index = useRef(0);
  const defaultContextMenuValues = { showContext: false, x: 0, y: 0 };
  const [contextMenuDetails, setContextMenuDetails] = useState(defaultContextMenuValues);
  const menuItems = [
    {
      name: 'Open in new tab',
      onclick: () => navigateToMembersPage(index.current, admin.owns, true),
    },
  ];
  const handleContextMenu = (e, indexValue) => {
    index.current = indexValue;
    e.preventDefault();
    setContextMenuDetails({
      showContext: true,
      x: e.pageX,
      y: e.pageY,
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'pinUnpin',
      headerName: '',
      width: 50,
      hideSortIcons: true,
      renderCell: (params) => {
        if (dashboardObj?.dashboardIds?.length) {
          return (
            <PinDashboardCollectionButton
              filters={null}
              entityId={params.row?.teamId}
              entityName={params.row?.name}
              homeUnpin={true}
              setCollect={true}
              dashbaordsCollectionIds={dashboardObj}
              isPinned={params.value}
            />
          );
        }
      },
      valueGetter: (params) => countMatchingItems(params) === dashboardObj?.dashboardIds?.length,
    },
    {
      field: 'name',
      headerName: 'Team Name',
      flex: 1,
      renderCell: (params) => {
        return (
          <Link
            component={RouterLink}
            to={`/team/team-members-view?teamId=${params?.row?.teamId}&teamName=${params?.row?.name}&goBackUrl=/workspace-users-and-teams`}
            underline="none"
          >
            <TeamTitleColumn param={params?.row?.name} />
          </Link>
        );
      },
    },
    {
      field: 'memberCount',
      headerName: 'Users',
      flex: 1,
      renderCell: (params) => (
        <Box
          onClick={() => {
            navigateToMembersPage(params?.row?.index, false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              navigateToMembersPage(params?.row?.index, admin.owns);
            }
          }}
          onContextMenu={(e) => handleContextMenu(e, params?.row?.index)}
          tabIndex={0}
          sx={{ cursor: 'pointer', alignItems: 'center' }}
        >
          {params.value}
        </Box>
      ),
    },
  ];

  const countMatchingItems = (params) =>
    pinnedDashboardCheck?.filter((item) => item === params.row?.teamId).length;

  if (!readOnlyMode) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <TeamListActions
          navigateToMembersPage={() => navigateToMembersPage(params.row.index)}
          openTeamNameEdit={() => openEdit(params.row.index)}
          openTeamSettings={() => openSettings(params.row.index)}
          openHistory={() => openHistory(params.row.index)}
        />
      ),
    });
  }

  if (admin.owns && isOrganizationalUnit) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => handleDeleteTeamMember(params.row)}>
          <Delete />
        </IconButton>
      ),
    });
  }

  async function handleDeleteTeamMember(rowObj: any) {
    const newresp = await selectedOrganizationalUnitTeams(rowObj.organizationalUnitId ?? '');
    newresp.data.forEach(async (item) => {
      if (rowObj.organizationalUnitId === item.organizationalId) {
        if (rowObj.teamId === item.teamId) {
          const payload = item.value;
          const res = await deleteOUTeamMember(payload);
          if (res.status === 200) {
            getTeamsInWorkspace();
            setNotification(success('Team removed from organization successfully!'));
          } else {
            setNotification(warning('Something went wrong!!'));
          }
        }
      }
    });
  }

  const data = useMemo(
    () =>
      filteredTeamList?.map((team, index) => ({
        id: team.id,
        index: index,
        teamId: team.id,
        name: team.name,
        memberCount: team.memberCount ? team.memberCount : 0,
        owner: team.ownerEmail,
        organizationalUnitId: organizationalUnitId,
      })) ?? [],
    [filteredTeamList, organizationalUnitId]
  );

  const getTeamsOfCurrentUser = useCallback(async () => {
    const response = await getMyTeams();
    teamsList.current = response.data;
    setFilteredTeamList(response.data);
  }, []);

  const getMembersOfTeam = async (team: ITeam) => {
    const teamId = team.id;
    const teamMembers: ITeamMembersResponse = {
      memberEmail: team.ownerEmail ?? '',
      name: '',
      role: 'owner',
      roles: null,
      teamId: team.id ?? '',
    };
    if (teamId) {
      const response = await getAllTeamMemberRecordsOfTeam(teamId, teamFilters);
      setTeamMembers([teamMembers, ...response.data.teamMembers]);
    } else {
      setTeamMembers([teamMembers]);
    }
  };

  const editTeamNameSuccessHandler = () => {
    setEditTeamNameSuccess(true);
    getTeamsInWorkspace();
  };

  const createTeamSuccessHandler = async (teamName?: string) => {
    setCreateTeamSuccess(true);
    await getTeamsInWorkspace();
    setNewTeamName(teamName);
  };

  const deleteMemberHandler = () => getMembersOfTeam(filteredTeamList[selectedRow] ?? {});

  useEffect(() => {
    isOrganizationalUnit || viewTeamAccess ? getTeamsInWorkspace() : getTeamsOfCurrentUser();
  }, [viewTeamAccess, getTeamsInWorkspace, getTeamsOfCurrentUser, isOrganizationalUnit]);

  useEffect(() => {
    if (newTeamName) {
      const newTeam = filteredTeamList.find((teamObj) => teamObj.name === newTeamName);
      if (newTeam)
        navigate('./team-members/' + newTeam.id, {
          state: {
            teamId: newTeam.id,
            teamName: newTeam.name,
            teamOwnerEmail: newTeam.ownerEmail,
          },
        });
    }
  }, [navigate, newTeamName, filteredTeamList]);

  const onTeamsNameSearch = (data) => {
    const teamName = data.teams?.toLowerCase();
    if (teamName) {
      const filteredRows =
        teamsList.current?.filter((row) => row?.name?.toLowerCase().includes(teamName)) ?? [];
      setFilteredTeamList(filteredRows);
    } else {
      setFilteredTeamList([...teamsList.current]);
    }
  };

  const generateNotification = (message, type) => (
    <Notification
      vertical="top"
      horizontal="center"
      autoHideDuration={3000}
      notificationObject={{ message, type }}
      notify={true}
    />
  );

  const paginationComponent = (
    <StyledPaginationBox>
      <Box ml={2} display="flex" gap={1}>
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>Total Count:</WsTypography>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold>
          {data.length}
        </WsTypography>
      </Box>
      {data.length > 10 && (
        <WsDataGridPagination
          count={Math.ceil(data.length / pageSize)}
          page={pageNo + 1}
          onPageChange={(_$, newPage) => setPageNo(newPage - 1)}
          pageSize={pageSize}
          onPageSizeChange={({ target: { value } }) => setPageSize(value)}
        />
      )}
    </StyledPaginationBox>
  );

  const onSortModelChange = (newSortModel: GridSortModel) => {
    setPageNo(0);
    setSortModel(newSortModel);
  };

  return (
    <Grid
      container
      bgcolor={WsColors.White}
      sx={{ boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)', borderRadius: 2, p: 2 }}
      overflow="hidden"
    >
      <Grid
        container
        item
        xs={12}
        sx={{ padding: '20px 20px 20px 25px' }}
        justifyContent="space-between"
      >
        <Grid item xs={8} display="flex" alignItems="center" gap={1.5}>
          <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
            My Teams
          </WsTypography>
          <Tooltip title="View user across different teams">
            <IconButton sx={{ p: 0 }}>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Grid>
        <Grid item xs={4} justifyItems="flex-end" sx={{ display: 'flex' }} gap={1}>
          {teamCreationAllowed && !readOnlyMode && (
            <CreateTeamButton
              onEditSuccess={editTeamNameSuccessHandler}
              onCreateSuccess={createTeamSuccessHandler}
              filteredTeamList={filteredTeamList}
              onFetch={getTeamsInWorkspace}
            />
          )}
          {organizationalUnitId && !isTeamsOrganizationalUnit && (
            <CreateTeamButton
              onEditSuccess={editTeamNameSuccessHandler}
              onCreateSuccess={createTeamSuccessHandler}
              organizationId={organizationalUnitId}
              filteredTeamList={filteredTeamList}
              onFetch={getTeamsInWorkspace}
              TeamsOrganizationalUnit={isTeamsOrganizationalUnit}
            />
          )}
        </Grid>
      </Grid>
      {contextMenuDetails.showContext && (
        <WsContextMenuHandler
          top={contextMenuDetails.y}
          left={contextMenuDetails.x}
          menuItems={menuItems}
          setContextMenuDetails={setContextMenuDetails}
        />
      )}
      <Grid xs={12}>
        {loader ? (
          <Box
            sx={{
              '& .MuiCard-root': {
                boxShadow: 'none',
                elevation: 0,
              },
            }}
          >
            <WsListingLoader />
          </Box>
        ) : (
          <FormProvider {...formMethods}>
            <Grid
              item
              xs={12}
              component="form"
              onSubmit={formMethods.handleSubmit(onTeamsNameSearch)}
            >
              <Grid
                bgcolor={WsColors.White}
                xs={5}
                sx={{
                  mx: 3,
                  marginBottom: 2,
                }}
              >
                <SearchBarNew name="teams" type="submit" size="small" />
              </Grid>
              {filteredTeamList && filteredTeamList?.length > 0 ? (
                <Grid item xs={12}>
                  {paginationComponent}
                  <StyledDataGrid
                    autoHeight
                    hideFooter
                    pageSize={pageSize}
                    page={pageNo}
                    columns={columns}
                    rows={data ?? []}
                    sortModel={sortModel}
                    onSortModelChange={onSortModelChange}
                  />
                  {data.length > 10 && paginationComponent}
                </Grid>
              ) : (
                <TeamsEmptyList
                  teamCreationAllowed={teamCreationAllowed ?? false}
                  isOrganizationalUnit={!readOnlyMode ? true : false}
                />
              )}
            </Grid>
          </FormProvider>
        )}
      </Grid>

      {filteredTeamList?.length > 0 && (
        <>
          <WsDrawer
            title={filteredTeamList[selectedRow].name}
            open={openTeamMembers}
            onClose={() => setOpenTeamMembers(false)}
          >
            <TeamMembersTable
              teamMembers={teamMembers || []}
              onDeleteMember={deleteMemberHandler}
            />
          </WsDrawer>
          <TeamSettings
            open={openTeamSettings}
            handleClose={() => setOpenTeamSettings(false)}
            team={filteredTeamList && filteredTeamList[selectedRow]}
          />
          <TeamHistory
            team={filteredTeamList && filteredTeamList[selectedRow]}
            open={openTeamHistory}
            handleClose={() => setOpenTeamHistory(false)}
          />
          <CreateTeamModal
            open={openEditTeamName}
            onEditSuccess={createTeamSuccessHandler}
            onCreateSuccess={createTeamSuccessHandler}
            handleClose={() => setOpenEditTeamName(false)}
            teamId={filteredTeamList[selectedRow].id ?? ''}
          />
        </>
      )}
      {createTeamSuccess && generateNotification('Team created successfully!', 'success')}
      {editTeamNameSuccess && generateNotification('Team name changed successfully!', 'success')}
      {openTeamMemberDrawer && <TeamAvatartGroup defaultTeam={filteredTeamList[selectedRow]} />}
    </Grid>
  );
};

export default TeamListNew;
